export const SecurityArchitectureQuestions = [
  {
    id: 'sec-arch-001',
    text: 'Which of the following best describes the principle of least privilege?',
    options: [
      'Networks should be segmented into VLANs',
      'All users should have administrative access',
      'Passwords should be changed every 90 days',
      'Users should have the minimum permissions necessary to perform their job functions'
    ],
    correctAnswer: 3,
    domain: 'Security Architecture',
    difficulty: 0.1,
    explanation: 'The principle of least privilege states that users should only have the minimum permissions necessary to perform their job functions. This reduces the attack surface and potential impact of compromised accounts.'
  },
  {
    id: 'sec-arch-002',
    text: 'Which HTTP security header can help mitigate clickjacking attacks?',
    options: [
      'Content-Security-Policy',
      'X-Frame-Options',
      'X-XSS-Protection',
      'Strict-Transport-Security'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'X-Frame-Options header prevents the webpage from being embedded in iframes on unauthorized domains, which is a common clickjacking technique.'
  },
  {
    id: 'sec-arch-003',
    text: 'In a zero trust architecture, what action must occur before ANY network resource access is granted?',
    options: [
      'User authentication',
      'Device authentication',
      'Both user and device verification',
      'Risk assessment'
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'Zero trust requires verification of both user identity and device security posture before granting access to resources. This comprehensive verification approach ensures both the user and the device meet security requirements.'
  },
  {
    id: 'sec-arch-004',
    text: 'To minimize the impact of increasing traffic types during an attack, a security engineer needs to configure a NGFW. What type of rule is most likely to be configured?',
    options: [
      'Port-based rules',
      'Behavioral-based rules',
      'Time-based rules',
      'Protocol-based rules'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'Behavioral-based rules in Next-Generation Firewalls can adapt to changing traffic patterns and better mitigate attacks compared to static rules.'
  },
  {
    id: 'sec-arch-005',
    text: 'What are two critical factors to consider in the design of high availability networks?',
    options: [
      'Cost and performance',
      'Ease of recovery and attack surface',
      'Bandwidth and latency',
      'Location and redundancy'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'When designing high availability networks, ease of recovery ensures quick restoration of services, while considering attack surface helps maintain security during failover scenarios.'
  },
  {
    id: 'sec-arch-006',
    text: 'Containers are a method of:',
    options: [
      'Data storage',
      'Virtualization',
      'Encryption',
      'Network segmentation'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.3,
    explanation: 'Containers are a method of virtualization that package applications and their dependencies together, allowing them to run consistently across different computing environments.'
  },
  {
    id: 'sec-arch-007',
    text: 'When setting up a cloud environment for an on-premises application migration, which security measure is most critical?',
    options: [
      'Virtualization and resource isolation',
      'Data encryption',
      'Strong authentication policies',
      'Network segmentation'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.9,
    explanation: 'Cloud computing relies on virtualization and resource isolation because it enables the core principle of multi-tenancy: sharing physical hardware resources among multiple, potentially unrelated, users or organizations. Without these technologies, sharing resources would be incredibly risky, as security breaches or resource exhaustion by one user could directly impact others.'
  },
  {
    id: 'sec-arch-008',
    text: 'When unusual outbound calls are traced to company phone numbers without spoofing, what is the most likely cause?',
    options: [
      'Weak SIP server settings',
      'Compromised voicemail system',
      'Social engineering attack',
      'Misconfigured call routing'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'Weak SIP (Session Initiation Protocol) server settings can allow unauthorized use of legitimate phone numbers without requiring number spoofing.'
  },
  {
    id: 'sec-arch-009',
    text: 'Which key difference distinguishes test environments from staging environments?',
    options: [
      'Test environments focus on functionality verification while staging mirrors production for final validation',
      'Test environments are public while staging is private',
      'Test environments use real data while staging uses mock data',
      'Test environments are automated while staging is manual'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.5,
    explanation: 'The main distinction is their purpose and configuration. Test environments are optimized for testing individual components and features, while staging environments replicate production to validate the system before deployment.'
  },
  {
    id: 'sec-arch-010',
    text: 'In a Platform as a Service (PaaS) model, what security aspect is typically managed by the customer rather than the provider?',
    options: [
      'Application security configuration',
      'Operating system patches',
      'Network infrastructure security',
      'Physical security'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'In PaaS, while the provider manages the platform infrastructure, the customer remains responsible for securing their applications, including configuration, access controls, and code security.'
  },
  {
    id: 'sec-arch-011',
    text: 'Which key difference distinguishes test environments from staging environments?',
    options: [
      'Test environments are automated while staging is manual',
      'Test environments use real data while staging uses mock data',
      'Test environments focus on functionality verification while staging mirrors production for final validation',
      'Test environments are public while staging is private'
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.5,
    explanation: 'The primary distinction is their purpose and configuration. Test environments are optimized for testing individual components and features, while staging environments closely replicate production to validate the complete system before deployment.'
  },
  {
    id: 'sec-arch-012',
    text: 'In a Platform as a Service (PaaS) model, what security aspect is typically managed by the customer rather than the provider?',
    options: [
      'Network infrastructure security',
      'Operating system patches',
      'Physical security',
      'Application security configuration'
    ],
    correctAnswer: 3,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'In PaaS, while the provider manages the platform infrastructure, the customer remains responsible for securing their applications, including configuration, access controls, and code security.'
  },
  {
    id: 'sec-arch-013',
    text: 'Which of the following is NOT a common security control for mitigating the risk of malware infections?',
    options: [
      'Antivirus software',
      'Intrusion Detection System (IDS)',
      'User training',
      'File integrity monitoring'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.4,
    explanation: 'While IDS can detect malicious activity, its primary focus is on network traffic and intrusion attempts, not directly preventing malware infections on endpoints.'
  },
  {
    id: 'sec-arch-013',
    text: 'Which of the following is NOT a common security control for mitigating the risk of malware infections?',
    options: [
      'Antivirus software',
      'Intrusion Detection System (IDS)',
      'User training',
      'File integrity monitoring'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.4,
    explanation: 'While IDS can detect malicious activity, its primary focus is on network traffic and intrusion attempts, not directly preventing malware infections on endpoints.'
  },
  {
    id: 'sec-arch-014',
    text: 'During a network scan, Telnet port 23 is found open on a legacy server, but the service supports encryption. What security control is likely in place?',
    options: [
      'Technical',
      'Compensating',
      'Administrative',
      'Preventive'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'Adding encryption to Telnet is a compensating control that helps mitigate the inherent security risks of the legacy protocol.'
  },
  {
    id: 'sec-arch-015',
    text: 'Which security control is most likely being utilized when a critical legacy server is isolated within a private network?',
    options: [
      'Detective',
      'Recovery',
      'Corrective',
      'Compensating'
    ],
    correctAnswer: 3,
    domain: 'Security Architecture',
    difficulty: 0.8,
    explanation: 'Network isolation serves as a compensating control for legacy systems that cannot be updated or replaced but must remain operational.'
  },
  {
    id: 'phys-sec-016',
    text: 'To enhance security at a data center, an administrator installs a CCTV system and posts surveillance warning signs. What best describes these types of controls?',
    options: [
      'Preventive and corrective',
      'Detective and deterrent',
      'Compensating and detective',
      'Recovery and deterrent'
    ],
    correctAnswer: 1,
    domain: 'Physical Security',
    difficulty: 0.1,
    explanation: 'CCTV is a detective control as it records incidents, while warning signs serve as deterrent controls by discouraging potential threats.'
  },
  {
    id: 'sec-arch-017',
    text: 'A security administrator discovers an application using MD5 for data storage. What vulnerability is likely present?',
    options: [
      'Buffer overflow',
      'Weak cryptographic hashing',
      'SQL injection',
      'Cross-site scripting'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.5,
    explanation: 'MD5 is cryptographically broken, making it vulnerable to collision attacks and unsuitable for secure data storage.'
  },
  {
    id: 'sec-arch-018',
    text: 'When implementing Full Disk Encryption (FDE) for organizational laptops, which two factors are most critical to consider?',
    options: [
      'Password complexity & backup storage',
      'Key escrow & TPM presence',
      'Encryption algorithm & key length',
      'User training & recovery procedures'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.9,
    explanation: 'Key escrow ensures organizational access to encrypted data if needed, while TPM provides secure key storage and hardware-based encryption support.'
  },
  {
    id: 'sec-arch-019',
    text: 'When an organization implements a VPN between headquarters and a branch location, what is the primary security benefit?',
    options: [
      'Geographic restriction bypass',
      'Data in transit protection',
      'Access control enhancement',
      'Network performance optimization'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.8,
    explanation: 'VPNs primarily protect data confidentiality and integrity while in transit between locations.'
  },
  {
    id: 'sec-arch-020',
    text: 'What comprehensive security concept is achieved by implementing a RADIUS server?',
    options: [
      'SSO',
      'MFA',
      'AAA',
      'PAM'
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'RADIUS provides Authentication, Authorization, and Accounting (AAA) services for network access control.'
  },
  {
    id: 'sec-arch-021',
    text: 'A company wants to implement a solution that provides secure storage and management of privileged credentials, such as passwords for administrative accounts and service accounts. Which of the following would be the MOST appropriate choice?',
    options: [
      'Shibboleth',
      'SIEM',
      'OpenID',
      'PAM',
    ],
    correctAnswer: 3,
    domain: 'Security Architecture',
    difficulty: 0.3,
    explanation: 'A Privileged Access Management (PAM) vault is designed specifically for securing and managing privileged credentials. It provides features like password rotation, access control, and auditing of privileged sessions.'
  },
  {
    id: 'sec-arch-022',
    text: 'A business needs to implement a software solution accessible to employees worldwide without the need for on-premises hardware or infrastructure management. Which cloud computing model best fits this requirement?',
    options: [
      'IaaS',
      'PaaS',
      'SaaS',
      'Hybrid Cloud'
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.9,
    explanation: 'Software as a Service (SaaS) provides ready-to-use applications over the internet, eliminating the need for local installation, hardware maintenance, or infrastructure management by the user. While IaaS and PaaS offer more control, they often involve some level of integration or management of underlying infrastructure.'
  },
  {
    id: 'sec-arch-023',
    text: 'A company decides to implement a new cloud-based HR system. They need to ensure that users can access this new system using their existing corporate credentials and single sign-on (SSO). Which technology should be configured to achieve this?',
    options: [
      'IPsec VPN',
      'SAML',
      'RADIUS',
      'Kerberos'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: "SAML (Security Assertion Markup Language) is commonly used to enable SSO between different security domains, such as a company’s on-premises network and a cloud-based application. It allows users to authenticate once and access multiple services without re-entering credentials. IPsec VPN creates a secure tunnel for network traffic but doesn't inherently provide SSO functionality. RADIUS is primarily used for network access authentication (like Wi-Fi) and not for SSO to web applications. Kerberos is an authentication protocol used within a single domain (like a Windows Active Directory environment) and is not typically used for SSO with cloud applications in the same way SAML is."
  },
  {
    id: 'sec-arch-024',
    text: "A financial firm employs remote contractors in multiple countries. To maintain data security without distributing company-owned hardware, which of the following is the MOST suitable solution?",
    options: [
      "VDI",
      "Distributing company laptops with VPN access",
      "Requiring contractors to use personal devices with strict security software",
      "Implementing a BYOD policy with basic security guidelines"
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.4,
    explanation: "VDI provides contractors with access to virtualized desktops hosted on the company's secure infrastructure, ensuring sensitive data remains within the corporate environment."
  },
  {
    id: 'sec-arch-025',
    text: "What architectural approach structures an application as a collection of small, independent services communicating through well-defined interfaces?",
    options: [
      "P2P",
      "Client-server architecture",
      "Microservices architecture using APIs",
      "Mainframe architecture"
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.1,
    explanation: "Microservices architecture promotes modularity, scalability, and independent deployment of services, often using APIs for communication."
  },
  {
    id: 'sec-arch-026',
    text: "In which scenario would a Next-Generation Firewall (NGFW) be most effectively utilized?",
    options: [
      "Filtering network traffic based solely on port and IP address.",
      "Providing basic network connectivity to remote users.",
      "Performing deep packet inspection and application control.",
      "Establishing secure VPN connections between branch offices."
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: "NGFWs excel at deep packet inspection, enabling granular control over applications and content. Simple port/IP filtering is a basic firewall function. Remote user connectivity is better handled by VPNs or other remote access solutions. While NGFWs can support VPNs, that's not their primary distinguishing feature."
  },
  {
    id: 'sec-arch-027',
    text: "When implementing single sign-on (SSO) using SAML or other similar protocols, which component is typically configured *before* the service provider (SP)?",
    options: [
      "The web browser.",
      "The Identity Provider.",
      "The network firewall.",
      "The database server."
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: "The Identity Provider (IdP) must be configured first, as it's responsible for authenticating users and issuing assertions that the SP will trust. The browser is the client, the firewall controls network access, and the database stores data; none of these are configured *before* the IdP in an SSO setup. The SP needs to know how to communicate with the IdP."
  },
  {
    id: 'sec-arch-028',
    text: 'An IT director is implementing file permissions where engineers can only access documents matching their security clearance level. Junior engineers should never see senior-level design documents. Which access control methodology fits these requirements?',
    options: [
      'Role-Based Access Control (RBAC)',
      'Mandatory Access Control (MAC)',
      'Discretionary Access Control (DAC)',
      'Attribute-Based Access Control (ABAC)'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'MAC is ideal for environments requiring strict hierarchical security levels, as it assigns both users and resources security classifications and enforces access based on these levels. Unlike RBAC or DAC, users cannot delegate their access rights to others.'
  },
  {
    id: 'sec-arch-029',
    text: 'An electric vehicle manufacturer needs to secure laptop hard drives against physical theft while ensuring the encryption keys cannot be easily compromised. Which hardware security feature provides the strongest protection for drive encryption?',
    options: [
      'Trusted Platform Module',
      'Self-encrypting drive',
      'Secure boot process',
      'UEFI password'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.8,
    explanation: 'TPM provides hardware-based security by storing encryption keys in tamper-resistant hardware and includes anti-brute force protections. The cryptographic keys are burned into the chip, making them extremely difficult to extract even if the drive is physically removed.'
  },
  {
    id: 'sec-arch-030',
    text: 'An organization wants to prevent compromised systems from affecting the entire network. Which architectural control would be most effective in limiting lateral movement?',
    options: [
      'Network segmentation',
      'Antivirus software',
      'SIEM',
      'Access control policies'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'Network segmentation divides a network into isolated segments, preventing unauthorized lateral movement between different parts of the network. This contains potential breaches and limits an attacker\'s ability to move freely through the infrastructure.'
  },
  {
    id: 'sec-arch-031',
    text: 'An organization deploys a solution that monitors data movement between on-premises systems and cloud services, providing real-time security policy enforcement. What technology is this?',
    options: [
      'Cloud Access Security Broker',
      'Web Application Firewall',
      'Security Information Event Management',
      'Data Loss Prevention'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'A Cloud Access Security Broker (CASB) specifically provides visibility and control over data moving between on-premises and cloud environments, including real-time monitoring and security policy enforcement. While other options handle security monitoring, they lack the cloud-specific focus of CASB.'
  },
  {
    id: 'sec-arch-032',
    text: 'A RAID 5 configuration requires a minimum of how many drives to implement its distributed parity feature?',
    options: [
      'Two drives',
      'Three drives',
      'Four drives',
      'Five drives'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'RAID 5 requires a minimum of three drives to implement striping with distributed parity. This provides both performance improvement and fault tolerance while optimizing storage efficiency.'
  },

  {
    id: 'sec-arch-033',
    text: 'In a high-performance storage implementation, which RAID level provides optimal read/write speeds but requires exactly two drives?',
    options: [
      'RAID 1',
      'RAID 0',
      'RAID 5',
      'RAID 10'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'RAID 0 requires exactly two drives for basic striping implementation. While it offers no redundancy, it provides maximum performance by striping data across both drives.'
  },

  {
    id: 'sec-arch-034',
    text: 'Which RAID configuration implements fault tolerance through data duplication using the minimum number of drives?',
    options: [
      'RAID 0',
      'RAID 1',
      'RAID 5',
      'RAID 6'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'RAID 1 requires a minimum of two drives to implement mirroring, providing complete data redundancy through exact duplication of data between drives.'
  },

  {
    id: 'sec-arch-035',
    text: 'A system administrator needs maximum fault tolerance for critical data. Which RAID level combines data striping with dual parity to protect against multiple drive failures?',
    options: [
      'RAID 10',
      'RAID 5',
      'RAID 6',
      'RAID 1'
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'RAID 6 uniquely implements double parity with striping, allowing the array to survive two simultaneous drive failures while maintaining performance benefits of striping.'
  },

  {
    id: 'sec-arch-036',
    text: 'Which RAID implementation combines the performance benefits of striping with the redundancy of mirroring?',
    options: [
      'RAID 5',
      'RAID 6',
      'RAID 0',
      'RAID 10'
    ],
    correctAnswer: 3,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'RAID 10 (also known as RAID 1+0) combines the performance advantages of striping from RAID 0 with the redundancy benefits of mirroring from RAID 1, providing both speed and fault tolerance.'
  },

  {
    id: 'sec-arch-037',
    text: 'Which RAID configurations implement data striping as part of their core functionality? (Select the most complete answer)',
    options: [
      'RAID 0 and RAID 6',
      'RAID 0, RAID 6, and RAID 10',
      'RAID 1 and RAID 10',
      'RAID 5 and RAID 6'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.8,
    explanation: 'RAID 0, RAID 6, and RAID 10 all implement data striping. RAID 0 uses basic striping, RAID 6 combines striping with double parity, and RAID 10 combines striping with mirroring.'
  },
  {
    id: 'sec-arch-038',
    text: 'Which security solution monitors and analyzes cloud service traffic from your internal network?',
    options: [
      'Web Application Firewall',
      'Cloud Access Security Broker',
      'Security Information and Event Management',
      'Next-Generation Firewall'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'A Cloud Access Security Broker (CASB) specifically analyzes traffic between internal networks and cloud services, unlike WAFs (which protect web applications), SIEM (which aggregates security events), or NGFW (which provides advanced firewall features).'
  },
  {
    id: 'sec-arch-039',
    text: 'An organization needs to establish a VPN connection through a highly restrictive corporate firewall. Which transport protocol configuration would be most appropriate for this scenario?',
    options: [
      'IPSec over TCP',
      'IPSec over UDP',
      'SSL over ICMP',
      'L2TP over ICMP'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'IPSec over TCP is most appropriate when VPN connections must traverse restrictive firewalls, as TCP traffic is more likely to be allowed through corporate firewalls compared to UDP or other protocols. While IPSec typically uses UDP, encapsulating it in TCP provides better firewall traversal capabilities.'
  },
  {
    id: 'sec-arch-040',
    text: 'Which authentication protocol is commonly integrated with 802.1X to enable enterprise-wide federated network access control across multiple buildings?',
    options: [
      'LDAP',
      'RADIUS',
      'Kerberos',
      'TACACS+'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'RADIUS (Remote Authentication Dial-In User Service) is commonly integrated with 802.1X to provide federated network access control. This combination allows for centralized authentication and authorization of wireless clients across multiple locations while maintaining consistent security policies.'
  },
  {
    id: 'sec-arch-041',
    text: 'In an enterprise wireless network deployment, which authentication framework supports federated access across multiple organizational boundaries?',
    options: [
      'WPA3-Personal',
      'WEP',
      'RADIUS',
      'WPA2-PSK'
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.5,
    explanation: 'RADIUS Federation, often used with 802.1X, enables secure, cross-organizational network access by forwarding authentication requests to the user\'s home organization\'s authentication server.'
  },
  {
    id: 'sec-arch-042',
    text: 'A corporation implements a mobile device policy that allows employees to choose from a pre-approved selection of company-owned devices for work use. Which mobility strategy does this represent?',
    options: [
      'Choose Your Own Device (CYOD)',
      'Bring Your Own Device (BYOD)',
      'Corporate Owned, Personally Enabled (COPE)',
      'Mobile Device Management (MDM)'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'Choose Your Own Device (CYOD) is a policy where organizations provide employees with a selection of pre-approved, company-owned devices to choose from. This differs from BYOD as it maintains corporate control over device selection and security while still offering employee choice.'
  },
  {
    id: 'sec-arch-043',
    text: 'What serves as a documented reference point for securing system configurations that defines required security settings, approved software, and necessary controls?',
    options: [
      'Security Configuration Baseline',
      'System Hardening Template',
      'Security Policy Document',
      'Change Management Log'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.4,
    explanation: 'A Security Configuration Baseline is a documented set of specifications for system components that defines the minimum security requirements, approved configurations, required security controls, and standard software versions that should be maintained for secure operation.'
  },
  {
    id: 'sec-arch-044',
    text: 'Which security protocol improved upon WEP by implementing TKIP and introducing a more robust key management system?',
    options: [
      'WPS',
      'WPA',
      'WEP2',
      'MAC filtering'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.5,
    explanation: 'Wi-Fi Protected Access (WPA) was developed as a replacement for WEP, introducing Temporal Key Integrity Protocol (TKIP) and stronger key management, though it was later superseded by WPA2.'
  },
  {
    id: 'sec-arch-045',
    text: 'Which software development approach integrates security practices throughout the entire DevOps pipeline?',
    options: [
      'DevSecOps',
      'Agile Security',
      'SecDevOps',
      'Continuous Integration (CI)'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'DevSecOps integrates security practices and testing throughout the entire development lifecycle, making security a shared responsibility across development, operations, and security teams.'
  },
  {
    id: 'sec-arch-046',
    text: 'In a software development pipeline, which environment is used for final testing before deployment to production?',
    options: [
      'Development',
      'Testing',
      'Staging',
      'QA'
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.4,
    explanation: 'The staging environment closely mirrors production and serves as the final testing ground before deployment, allowing verification of application behavior in a production-like setting while maintaining separation from actual production systems.'
  },
  {
    id: 'sec-arch-047',
    text: 'Which practice involves automatically building and testing code changes when developers commit updates to version control?',
    options: [
      'Continuous Integration',
      'Continuous Deployment',
      'Continuous Monitoring',
      'Continuous Security'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.5,
    explanation: 'Continuous Integration (CI) automatically validates code changes through building and testing when developers commit updates, helping identify integration issues early in the development process.'
  },
  {
    id: 'sec-arch-048',
    text: 'Which standardized format is used for sharing cyber threat intelligence between organizations?',
    options: [
      'STIX',
      'JSON',
      'XML',
      'YAML'
    ],
    correctAnswer: 0,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'Structured Threat Information Expression (STIX) is a standardized language for sharing cyber threat intelligence, enabling organizations to share structured threat information.'
  },
  {
    id: 'sec-arch-049',
    text: 'Which framework provides a comprehensive matrix of adversary tactics and techniques used in cyber attacks?',
    options: [
      'ISO 27001',
      'NIST CSF',
      'MITRE ATT&CK',
      'CIS Controls'
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.8,
    explanation: 'The MITRE ATT&CK framework provides a comprehensive knowledge base of adversary tactics and techniques based on real-world observations, helping organizations improve their security posture.'
  },
  {
    id: 'sec-arch-050',
    text: 'What protocol enables automated threat intelligence sharing between organizations?',
    options: [
      'REST',
      'TAXII',
      'SMTP',
      'MQTT'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'Trusted Automated Exchange of Intelligence Information (TAXII) is a protocol designed for exchanging cyber threat intelligence between organizations using standardized services and message exchanges.'
  },
  {
    id: 'sec-arch-051',
    text: 'Which security measure replaces sensitive data with unique identification symbols while maintaining the data\'s business utility?',
    options: [
      'Encryption',
      'Hashing',
      'Tokenization',
      'Masking'
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.6,
    explanation: 'Tokenization substitutes sensitive data with non-sensitive placeholders (tokens), maintaining data format and usability while protecting the original information.'
  },
  {
    id: 'sec-arch-052',
    text: 'Which exploit prevention technology prevents code execution from memory regions marked as non-executable?',
    options: [
      'ASLR',
      'DEP',
      'Stack canaries',
      'Control flow integrity'
    ],
    correctAnswer: 1,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'Data Execution Prevention (DEP) marks certain memory regions as non-executable, helping prevent buffer overflow attacks that attempt to execute malicious code from these areas.'
  },
  {
    id: 'sec-arch-053',
    text: 'An organization implements security cameras that are clearly visible but non-functional alongside working cameras. This PRIMARILY serves as what type of control?',
    options: [
      'Preventive control',
      'Detective control',
      'Deterrent control',
      'Compensating control'
    ],
    correctAnswer: 2,
    domain: 'Security Architecture',
    difficulty: 0.7,
    explanation: 'While this might seem like a preventive control, it\'s primarily a deterrent control because its main purpose is to discourage potential threats through the appearance of security, rather than actually preventing or detecting incidents.'
  },
  {
    id: 'sec-arch-054',
    text: 'A high-security facility implemented thermal imaging after biometric scanners proved unreliable in extreme cold. What type of control is the thermal imaging system?',
    options: [
      'Alternative control',
      'Deterrent control',
      'Supplemental control',
      'Compensating control'
    ],
    correctAnswer: 3,
    domain: 'Security Architecture',
    difficulty: 0.8,
    explanation: 'This is a compensating control because it provides an alternative method to achieve similar security objectives when the primary control (biometric scanners) cannot function as intended due to environmental constraints.'
  },
];